import { Link } from 'react-router-dom';
import { useLayoutEffect } from 'react';
import { useSlidingUnderline } from '../ui/useSlidingUnderline';
import { useReducedMotion } from '../ui/useReducedMotion';

/** Persistent campaign destinations shared by the overview and question editor. */
export type CampaignTab = 'analytics' | 'responses' | 'questions' | 'automate';

/** Compact navigation keeps questions and recipes one click from every campaign. */
export function CampaignNavigation({ sessionId, active, responseCount, canEdit = true, onSelect }: {
  sessionId: string;
  active: CampaignTab;
  responseCount?: number;
  canEdit?: boolean;
  onSelect?: (tab: CampaignTab) => void;
}) {
  const reduced = useReducedMotion();
  const tabs: Array<{ value: CampaignTab; label: string }> = [
    { value: 'analytics', label: 'Analytics' },
    { value: 'responses', label: 'Responses' },
    ...(canEdit ? [{ value: 'questions' as const, label: 'Questions' }] : []),
    { value: 'automate', label: 'Automate' },
  ];
  const index = tabs.findIndex(tab => tab.value === active);
  const { listRef, setTabRef, indicatorStyle } = useSlidingUnderline(index, tabs.length);
  useLayoutEffect(() => {
    const viewport = listRef.current?.parentElement;
    if (!viewport) return undefined;
    /** Contextual actions may resize the row after its active tab has mounted. */
    const revealActive = () => {
      const selected = listRef.current?.querySelector<HTMLElement>('[aria-current="page"]');
      if (!selected || !viewport.clientWidth) return;
      const right = selected.offsetLeft + selected.offsetWidth;
      if (selected.offsetLeft < viewport.scrollLeft) viewport.scrollLeft = selected.offsetLeft;
      else if (right > viewport.scrollLeft + viewport.clientWidth) viewport.scrollLeft = right - viewport.clientWidth;
    };
    revealActive();
    const observer = new ResizeObserver(revealActive);
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [active, listRef]);
  return (
    <nav aria-label="Campaign" className="min-w-0 flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <div ref={listRef} className="relative flex w-max gap-1 sm:gap-2">
        {tabs.map((tab, tabIndex) => (
          <Link key={tab.value} ref={setTabRef(tabIndex)}
            to={tab.value === 'questions' ? `/admin/interviews/${sessionId}/edit` : `/admin/interviews/${sessionId}${tab.value === 'analytics' ? '' : `?tab=${tab.value}`}`}
            aria-current={active === tab.value ? 'page' : undefined}
            aria-label={tab.value === 'responses' && responseCount !== undefined ? `Responses ${responseCount}` : tab.label}
            onClick={event => {
              if (onSelect && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onSelect(tab.value); }
            }}
            className={`flex shrink-0 cursor-pointer items-center gap-2 rounded-lg px-2 sm:px-3 py-3 text-sm font-medium transition-colors duration-300 motion-reduce:transition-none outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foreground ${active === tab.value ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            {tab.label}
            {tab.value === 'responses' && responseCount !== undefined && <span className="text-muted-foreground">{responseCount}</span>}
          </Link>
        ))}
        <span aria-hidden="true" className="absolute bottom-0 left-0 h-[2px] rounded-full bg-foreground" style={{ ...indicatorStyle, ...(reduced ? { transition: 'none' } : {}) }} />
      </div>
    </nav>
  );
}
